import { HierarchyNode } from 'core/API/hierarchy';
import {
	getCurrentUser,
	getCurrentUserHierarchyId,
	getPermittedHierarchies,
} from 'module/onboarding/utils/hierarchyPermissions';
import { formatWorkplaceUnitNumber } from 'shared/utils/stringUtils';
import { DEFAULT_SECURITY_ROLES } from '../constants';
import { findWorkplaceHierarchyNode } from './filterUserSettingsUsers';

export interface CurrentUserRoleLike {
	name?: string;
	securityRoleType?: { label?: string } | string;
}

const ORG_ADMIN_PATTERN = /organ[i]sation.*admin/i;
const ORGANIZATION_HIERARCHY_TYPE = '/api/hierarchy_types/1';

const getSecurityRoleTypeLabel = (securityRoleType?: CurrentUserRoleLike['securityRoleType']): string => {
	if (!securityRoleType) return '';
	if (typeof securityRoleType === 'string') return securityRoleType;
	return securityRoleType.label ?? '';
};

export const getHierarchyResourceNumericId = (resourceId?: string | null): number | null => {
	if (!resourceId) return null;
	const parts = String(resourceId).split('/').filter(Boolean);
	const last = Number(parts[parts.length - 1]);
	return Number.isNaN(last) ? null : last;
};

export const hierarchyResourcesMatch = (left?: string | null, right?: string | null): boolean => {
	if (!left || !right) return false;
	if (left === right) return true;
	const leftId = getHierarchyResourceNumericId(left);
	const rightId = getHierarchyResourceNumericId(right);
	return leftId != null && leftId === rightId;
};

export const isOrganizationAdminRole = (
	role: CurrentUserRoleLike | null | undefined
): boolean => {
	if (!role) return false;

	const typeLabel = getSecurityRoleTypeLabel(role.securityRoleType);
	const roleName = role.name ?? '';

	return (
		typeLabel === DEFAULT_SECURITY_ROLES.ORGANISATION_ADMIN ||
		roleName === DEFAULT_SECURITY_ROLES.ORGANISATION_ADMIN ||
		ORG_ADMIN_PATTERN.test(typeLabel) ||
		ORG_ADMIN_PATTERN.test(roleName)
	);
};

export const isOrganizationAdmin = (currentUserRole: CurrentUserRoleLike | null): boolean =>
	isOrganizationAdminRole(currentUserRole);

export const resolveCurrentUserRole = (
	storeRole: CurrentUserRoleLike | null
): CurrentUserRoleLike | null => {
	if (storeRole) return storeRole;

	try {
		const storedRole = localStorage.getItem('BYOD-user-role');
		if (storedRole) {
			return JSON.parse(storedRole);
		}

		const currentUser = getCurrentUser();
		return currentUser?.securityRole ?? null;
	} catch {
		// ignore parse errors
	}

	return null;
};

export const getCurrentUserHierarchy = () => getCurrentUser()?.hierarchy ?? null;

const getHierarchyTypeChildId = (hierarchy: Record<string, unknown> | null | undefined): string => {
	if (!hierarchy) return '';
	const typeChild = hierarchy.hierarchyTypeChild;
	if (typeof typeChild === 'string') return typeChild;
	if (typeChild && typeof typeChild === 'object' && '@id' in typeChild) {
		return String((typeChild as { '@id'?: string })['@id'] ?? '');
	}
	return '';
};

export const isOrganizationHierarchyAssignment = (
	hierarchy: Record<string, unknown> | null | undefined
): boolean => {
	if (!hierarchy) return false;

	const parentId = hierarchy.parentId;
	if (parentId == null || parentId === 'null') {
		return true;
	}

	if (getHierarchyTypeChildId(hierarchy) === ORGANIZATION_HIERARCHY_TYPE) {
		return true;
	}

	const name = String(hierarchy.name ?? '').toLowerCase();
	return name.includes('organization') || name.includes('organisation');
};

export const isCurrentUserAtOrganizationHierarchy = (
	hierarchyNodes: HierarchyNode[]
): boolean => {
	const userHierarchy = getCurrentUserHierarchy();
	if (isOrganizationHierarchyAssignment(userHierarchy)) {
		return true;
	}

	const hierarchyId = getCurrentUserHierarchyId();
	if (hierarchyId == null) return false;

	const node = hierarchyNodes.find((entry) => entry.id === hierarchyId);
	return node?.type === 'organization' || node?.parentId == null;
};

/**
 * True when the logged-in user may assign users to any hierarchy level.
 */
export const canSelectAnyHierarchyLevel = (
	currentUserRole: CurrentUserRoleLike | null,
	hierarchyNodes: HierarchyNode[]
): boolean =>
	isOrganizationAdmin(currentUserRole) || isCurrentUserAtOrganizationHierarchy(hierarchyNodes);

/**
 * Returns hierarchy options for user create/edit forms.
 * Users may assign hierarchies at or below their own level (descendants, including self).
 */
export const getUserFormHierarchyOptions = (
	hierarchyNodes: HierarchyNode[],
	workplaceId: number,
	currentUserRole: CurrentUserRoleLike | null
): HierarchyNode[] => {
	if (canSelectAnyHierarchyLevel(currentUserRole, hierarchyNodes)) {
		return hierarchyNodes;
	}

	const currentUserHierarchyId = getCurrentUserHierarchyId();
	if (currentUserHierarchyId != null) {
		return getPermittedHierarchies(currentUserHierarchyId, hierarchyNodes);
	}

	const workplaceNode = findWorkplaceHierarchyNode(hierarchyNodes, workplaceId);
	if (workplaceNode) {
		return [workplaceNode];
	}

	return hierarchyNodes.filter(
		(node) => Number(node.childId) === workplaceId
	);
};

/**
 * True when the hierarchy field should be editable on create/edit user forms.
 */
export const canEditUserFormHierarchy = (
	currentUserRole: CurrentUserRoleLike | null,
	hierarchyNodes: HierarchyNode[],
	hierarchyOptions: HierarchyNode[]
): boolean =>
	canSelectAnyHierarchyLevel(currentUserRole, hierarchyNodes) || hierarchyOptions.length > 1;

export const getHierarchyNodeResourceId = (
	node: HierarchyNode & { '@id'?: string }
): string => node['@id'] ?? `/api/hierarchy_nodes/${node.id}`;

export const findHierarchyNodeByResourceId = (
	hierarchyNodes: Array<HierarchyNode & { '@id'?: string }>,
	resourceId?: string | null
): (HierarchyNode & { '@id'?: string }) | undefined => {
	if (!resourceId) return undefined;

	const numericId = getHierarchyResourceNumericId(resourceId);
	return hierarchyNodes.find((node) => {
		if (hierarchyResourcesMatch(getHierarchyNodeResourceId(node), resourceId)) {
			return true;
		}
		return numericId != null && node.id === numericId;
	});
};

export type UserHierarchyLike = {
	'@id'?: string;
	'@type'?: string;
	id: number;
	name: string;
	childId: string | null;
	editable: boolean;
	hierarchyTypeChild: string | null;
	hierarchyTypeParent: string;
	parentId: string | null;
};

/**
 * Build a User.hierarchy object from a form hierarchy resource id + hierarchy nodes.
 * Used after create/update so the settings store keeps a full hierarchy (name + @id),
 * even when the API returns only an IRI string.
 */
export const buildUserHierarchyFromResource = (
	hierarchyResourceId: string | null | undefined,
	hierarchyNodes: Array<HierarchyNode & { '@id'?: string }>,
	fallback?: UserHierarchyLike | null
): UserHierarchyLike | null => {
	if (!hierarchyResourceId) {
		return fallback ?? null;
	}

	const node = findHierarchyNodeByResourceId(hierarchyNodes, hierarchyResourceId);
	const numericId =
		node?.id ?? getHierarchyResourceNumericId(hierarchyResourceId) ?? null;

	if (numericId == null && !node) {
		return fallback ?? null;
	}

	const fallbackMatches =
		!!fallback &&
		(hierarchyResourcesMatch(fallback['@id'], hierarchyResourceId) ||
			(numericId != null && fallback.id === numericId));

	return {
		'@id': hierarchyResourceId,
		'@type': 'Hierarchy',
		id: numericId ?? fallback?.id ?? 0,
		name: node?.name || (fallbackMatches ? fallback?.name : '') || '',
		childId:
			node?.childId != null
				? String(node.childId)
				: fallbackMatches && fallback?.childId != null
					? String(fallback.childId)
					: null,
		editable: node?.editable ?? (fallbackMatches ? fallback?.editable : false) ?? false,
		hierarchyTypeChild:
			(node?.hierarchyTypeChild as { '@id'?: string } | undefined)?.['@id'] ??
			(fallbackMatches && typeof fallback?.hierarchyTypeChild === 'string'
				? fallback.hierarchyTypeChild
				: null),
		hierarchyTypeParent:
			(node?.hierarchyTypeParent as { '@id'?: string } | undefined)?.['@id'] ??
			(fallbackMatches && typeof fallback?.hierarchyTypeParent === 'string'
				? fallback.hierarchyTypeParent
				: '') ??
			'',
		parentId:
			node?.parentId != null
				? String(node.parentId)
				: fallbackMatches && fallback?.parentId != null
					? String(fallback.parentId)
					: null,
	};
};

/**
 * Returns true when the user may submit with the given hierarchy value.
 * On edit, an unchanged hierarchy is always permitted (preserves existing assignment).
 */
export const isHierarchyAssignmentPermitted = (
	hierarchyResourceId: string | undefined | null,
	hierarchyOptions: HierarchyNode[],
	originalHierarchyResourceId?: string | null
): boolean => {
	if (!hierarchyResourceId) return false;

	if (
		originalHierarchyResourceId &&
		hierarchyResourcesMatch(hierarchyResourceId, originalHierarchyResourceId)
	) {
		return true;
	}

	return Boolean(findHierarchyNodeByResourceId(hierarchyOptions, hierarchyResourceId));
};

export const getCurrentWorkplaceHierarchyNode = (
	hierarchyNodes: HierarchyNode[],
	workplaceId: number
): HierarchyNode | undefined => findWorkplaceHierarchyNode(hierarchyNodes, workplaceId);

export const sortHierarchyOptionsForDisplay = (
	hierarchyNodes: HierarchyNode[],
	cleanHierarchyName: (raw?: string) => string
): HierarchyNode[] =>
	[...hierarchyNodes].sort((a, b) => {
		const getTypePriority = (type: string) => {
			if (type === 'organization') return 0;
			if (type === 'group' || type === 'custom') return 1;
			if (type === 'workplace') return 2;
			return 3;
		};

		const aPriority = getTypePriority(a.type);
		const bPriority = getTypePriority(b.type);
		if (aPriority !== bPriority) return aPriority - bPriority;

		if ((a.type === 'group' || a.type === 'custom') && (b.type === 'group' || b.type === 'custom')) {
			return cleanHierarchyName(a.name).localeCompare(cleanHierarchyName(b.name), undefined, {
				sensitivity: 'base',
			});
		}

		if (a.type === 'workplace' && b.type === 'workplace') {
			const childDiff = Number(a.childId) - Number(b.childId);
			if (childDiff !== 0) return childDiff;
		}

		return cleanHierarchyName(a.name).localeCompare(cleanHierarchyName(b.name), undefined, {
			sensitivity: 'base',
		});
	});

/**
 * Extracts a workplace unit number from a hierarchy node name like "Tavern 101 Test(2002)".
 * Parentheses with the unit exist only on workplace nodes.
 */
export const extractUnitNumberFromHierarchyName = (raw?: string): string | null => {
	const match = (raw || '').match(/\(([^)]+)\)/);
	const extracted = match?.[1]?.trim();
	if (!extracted || !/^\d+$/.test(extracted)) return null;
	return formatWorkplaceUnitNumber(extracted);
};

const prefixFromChildId = (option: HierarchyNode): string =>
	formatWorkplaceUnitNumber(option.childId ?? option.id);

export const getHierarchyOptionLabel = (
	option: HierarchyNode | string,
	cleanHierarchyName: (raw?: string) => string
): string => {
	if (typeof option === 'string') return option;
	const cleaned = cleanHierarchyName(option.name);

	if (option.type === 'organization') return cleaned;

	// Workplace: parentheses unit first; custom/group (and workplace miss): childId
	const fromName =
		option.type === 'workplace' ? extractUnitNumberFromHierarchyName(option.name) : null;
	const prefix = fromName ?? prefixFromChildId(option);
	return `${prefix} - ${cleaned}`;
};
